import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getMailMessage, getMailThread, setMailReadState, downloadMailAttachmentBlob,
} from "../../services/supportMail";
import { IMailDetail, IMailThread, IMailAttachment, IMailParticipant } from "../../models/ISupportMail";
import SandboxedMailBody from "./SandboxedMailBody";
import ConvertToTicketModal from "./ConvertToTicketModal";
import { STATUS_META, FILTER_REASON_LABEL } from "./mailStatusMeta";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let val = bytes / 1024;
  let i = 0;
  while (val >= 1024 && i < units.length - 1) { val /= 1024; i++; }
  return `${val.toLocaleString("tr-TR", { maximumFractionDigits: 1 })} ${units[i]}`;
}

function participantLabel(p: IMailParticipant): string {
  return p.name ? `${p.name} <${p.email}>` : p.email;
}

function AttachmentRow({ messageId, att }: { messageId: string; att: IMailAttachment }) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    setError(false);
    try {
      const blob = await downloadMailAttachmentBlob(messageId, att.attachmentId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch {
      setError(true);
    } finally {
      setDownloading(false);
    }
  };

  const isImg = att.mimeType.startsWith("image/");
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#f8faff", border: "1.5px solid #e0e7ff", borderRadius: 10, padding: "8px 12px" }}>
      <i className={`bi ${isImg ? "bi-image" : "bi-file-earmark-fill"}`} style={{ color: isImg ? "#10b981" : "#6366f1", fontSize: "1rem", flexShrink: 0 }}></i>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: "0.82rem", fontWeight: 600, color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{att.fileName}</div>
        <div style={{ fontSize: "0.7rem", color: "#9ca3af" }}>{formatBytes(att.sizeBytes)}</div>
      </div>
      {error && <span style={{ fontSize: "0.72rem", color: "#dc2626" }}>Hata</span>}
      <button onClick={handleDownload} disabled={downloading} title="İndir" style={{
        width: 30, height: 30, borderRadius: 8, border: "1px solid #bfdbfe", background: "#eff6ff",
        color: "#1d4ed8", cursor: downloading ? "wait" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        {downloading ? <span className="spinner-border spinner-border-sm" style={{ width: 12, height: 12 }} /> : <i className="bi bi-download" style={{ fontSize: "0.78rem" }}></i>}
      </button>
    </div>
  );
}

function ThreadMessageCard({ msg }: { msg: IMailThread["messages"][number] }) {
  const isOut = msg.direction === "Outgoing";
  return (
    <div style={{
      borderRadius: 12, border: `1.5px solid ${isOut ? "#c7d2fe" : "#f1f5f9"}`,
      background: isOut ? "#f8faff" : "#fff", overflow: "hidden",
    }}>
      <div style={{ padding: "9px 13px", display: "flex", alignItems: "center", gap: 8, borderBottom: "1px solid #f1f5f9", flexWrap: "wrap" }}>
        <span style={{
          fontSize: "0.68rem", fontWeight: 700, padding: "2px 9px", borderRadius: 20,
          background: isOut ? "#eef2ff" : "#f0fdf4", color: isOut ? "#4338ca" : "#166534",
        }}>
          <i className={`bi ${isOut ? "bi-arrow-up-right-circle-fill" : "bi-arrow-down-left-circle-fill"} me-1`}></i>
          {isOut ? "Giden" : "Gelen"}
        </span>
        <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>
          {participantLabel(msg.from)}
        </span>
        <span style={{ fontSize: "0.72rem", color: "#9ca3af", flexShrink: 0 }}>{formatDate(msg.receivedAt)}</span>
      </div>
      <div style={{ maxHeight: 300, overflowY: "auto" }}>
        <SandboxedMailBody html={msg.bodyHtml} />
      </div>
    </div>
  );
}

interface Props {
  messageId: string;
  onClose: () => void;
  onChanged: () => void;
  isMobile: boolean;
}

export default function MailDetailPanel({ messageId, onClose, onChanged, isMobile }: Props) {
  const navigate = useNavigate();
  const [mail, setMail] = useState<IMailDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [markingRead, setMarkingRead] = useState(false);

  const [showThread, setShowThread] = useState(false);
  const [thread, setThread] = useState<IMailThread | null>(null);
  const [threadLoading, setThreadLoading] = useState(false);

  const [showConvert, setShowConvert] = useState(false);
  const [convertedTicket, setConvertedTicket] = useState<{ ticketId: string; ticketNo: string; title: string } | null>(null);

  useEffect(() => {
    setMail(null);
    setError("");
    setLoading(true);
    setShowThread(false);
    setThread(null);
    setConvertedTicket(null);
    getMailMessage(messageId)
      .then((res) => setMail(res.data))
      .catch(() => setError("Mail yüklenemedi."))
      .finally(() => setLoading(false));
  }, [messageId]);

  const toggleRead = async () => {
    if (!mail) return;
    setMarkingRead(true);
    try {
      await setMailReadState([mail.id], !mail.isRead);
      setMail({ ...mail, isRead: !mail.isRead });
      onChanged();
    } catch {
      setError("Okundu durumu güncellenemedi.");
    } finally {
      setMarkingRead(false);
    }
  };

  const toggleThread = () => {
    if (showThread) { setShowThread(false); return; }
    setShowThread(true);
    if (!thread && mail) {
      setThreadLoading(true);
      getMailThread(mail.threadId)
        .then((res) => setThread(res.data))
        .catch(() => setError("Konuşma yüklenemedi."))
        .finally(() => setThreadLoading(false));
    }
  };

  const panelStyle: React.CSSProperties = isMobile
    ? { position: "fixed", inset: 0, zIndex: 3000, background: "#fff", display: "flex", flexDirection: "column", overflow: "hidden" }
    : { position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 3000, width: "min(620px, 92vw)", background: "#fff", boxShadow: "-8px 0 40px rgba(0,0,0,0.16)", display: "flex", flexDirection: "column", overflow: "hidden" };

  const ticket = mail?.ticket ?? convertedTicket;
  const status = mail ? (STATUS_META[convertedTicket ? "TicketCreated" : mail.status] ?? STATUS_META.NotProcessed) : null;
  const nonInlineAttachments = mail?.attachments.filter((a) => !a.isInline) ?? [];

  return (
    <>
      {!isMobile && <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 2999, background: "rgba(0,0,0,0.35)" }} />}
      <div style={panelStyle}>
        {/* Header */}
        <div style={{ padding: isMobile ? "14px 16px" : "16px 20px", borderBottom: "1.5px solid #f1f5f9", display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
          <button onClick={onClose} style={{ background: "#f1f5f9", border: "none", borderRadius: 8, width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#374151", flexShrink: 0 }}>
            <i className={`bi ${isMobile ? "bi-arrow-left" : "bi-x-lg"}`}></i>
          </button>
          <div style={{ flex: 1, minWidth: 0, fontWeight: 800, fontSize: "0.94rem", color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {mail?.subject || "Mail"}
          </div>
          {mail && (
            <button onClick={toggleRead} disabled={markingRead} title={mail.isRead ? "Okunmadı yap" : "Okundu yap"} style={{
              width: 34, height: 34, borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff",
              color: "#6366f1", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <i className={`bi ${mail.isRead ? "bi-envelope-open" : "bi-envelope-fill"}`}></i>
            </button>
          )}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? 16 : 20 }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: "60px 0" }}><span className="spinner-border text-primary" /></div>
          ) : error && !mail ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#dc2626" }}>
              <i className="bi bi-exclamation-circle-fill" style={{ fontSize: "1.8rem", display: "block", marginBottom: 8 }}></i>
              {error}
            </div>
          ) : mail && (
            <>
              {error && (
                <div style={{ marginBottom: 14, padding: "8px 12px", borderRadius: 9, background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", fontSize: "0.8rem" }}>{error}</div>
              )}

              {/* Badges */}
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
                {status && (
                  <span style={{ fontSize: "0.72rem", fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: status.bg, color: status.color, border: `1px solid ${status.border}` }}>
                    {status.label}
                  </span>
                )}
                {mail.filterReason && (
                  <span style={{ fontSize: "0.72rem", fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: "#f9fafb", color: "#6b7280", border: "1px solid #e5e7eb" }}>
                    {FILTER_REASON_LABEL[mail.filterReason.code] ?? mail.filterReason.code}: {mail.filterReason.detail}
                  </span>
                )}
                {mail.sender.isRegisteredUser && (
                  <span style={{ fontSize: "0.72rem", fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: "#f5f3ff", color: "#7c3aed", border: "1px solid #ddd6fe" }}>
                    <i className="bi bi-person-check-fill me-1"></i>Kayıtlı kullanıcı
                  </span>
                )}
              </div>

              {/* Ticket link */}
              {ticket && (
                <button onClick={() => navigate(`/ticket/${ticket.ticketId}`)} style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "11px 14px", marginBottom: 14,
                  borderRadius: 12, background: "#f0fdf4", border: "1.5px solid #bbf7d0", cursor: "pointer", textAlign: "left",
                }}>
                  <i className="bi bi-ticket-perforated-fill" style={{ color: "#16a34a", fontSize: "1.1rem", flexShrink: 0 }}></i>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "#166534", textTransform: "uppercase" }}>{ticket.ticketNo}</div>
                    <div style={{ fontSize: "0.84rem", fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ticket.title}</div>
                  </div>
                  <i className="bi bi-chevron-right" style={{ color: "#16a34a", flexShrink: 0 }}></i>
                </button>
              )}

              {/* From/To/Cc */}
              <div style={{ background: "#f8faff", border: "1.5px solid #e0e7ff", borderRadius: 12, padding: 14, marginBottom: 14, display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ fontSize: "0.82rem" }}><strong style={{ color: "#6b7280" }}>Kimden:</strong> <span style={{ color: "#111827" }}>{participantLabel(mail.from)}</span></div>
                <div style={{ fontSize: "0.82rem", overflow: "hidden", textOverflow: "ellipsis" }}><strong style={{ color: "#6b7280" }}>Kime:</strong> <span style={{ color: "#111827" }}>{mail.to.map(participantLabel).join(", ")}</span></div>
                {mail.cc.length > 0 && (
                  <div style={{ fontSize: "0.82rem" }}><strong style={{ color: "#6b7280" }}>Cc:</strong> <span style={{ color: "#111827" }}>{mail.cc.map(participantLabel).join(", ")}</span></div>
                )}
                <div style={{ fontSize: "0.76rem", color: "#9ca3af" }}><i className="bi bi-clock me-1"></i>{formatDate(mail.receivedAt)}</div>
              </div>

              {/* Body */}
              <div style={{ border: "1.5px solid #f1f5f9", borderRadius: 12, overflow: "hidden", marginBottom: 14 }}>
                <SandboxedMailBody html={mail.bodyHtml} />
              </div>

              {/* Attachments */}
              {nonInlineAttachments.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 8 }}>
                    <i className="bi bi-paperclip me-1"></i>Ekler ({nonInlineAttachments.length})
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {nonInlineAttachments.map((att) => <AttachmentRow key={att.attachmentId} messageId={mail.id} att={att} />)}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
                <button onClick={toggleThread} style={{
                  flex: "1 1 auto", padding: "10px 14px", borderRadius: 10, border: "1.5px solid #e5e7eb",
                  background: showThread ? "#eef2ff" : "#fff", color: "#4338ca", fontWeight: 700, fontSize: "0.84rem", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                }}>
                  <i className={`bi ${showThread ? "bi-chevron-up" : "bi-chat-left-text-fill"}`}></i>
                  Konuşmayı {showThread ? "Gizle" : "Gör"}
                </button>
                {!convertedTicket && !mail.ticket && mail.canConvertToTicket && (
                  <button onClick={() => setShowConvert(true)} style={{
                    flex: "1 1 auto", padding: "10px 14px", borderRadius: 10, border: "none",
                    background: "linear-gradient(135deg, #7c3aed, #6d28d9)", color: "#fff", fontWeight: 700, fontSize: "0.84rem", cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                  }}>
                    <i className="bi bi-arrow-repeat"></i> Ticket'a Çevir
                  </button>
                )}
              </div>

              {/* Thread */}
              {showThread && (
                <div style={{ marginBottom: 8 }}>
                  {threadLoading ? (
                    <div style={{ textAlign: "center", padding: "24px 0" }}><span className="spinner-border spinner-border-sm text-secondary" /></div>
                  ) : thread ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {thread.messages.map((m) => <ThreadMessageCard key={m.id} msg={m} />)}
                    </div>
                  ) : null}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showConvert && mail && (
        <ConvertToTicketModal
          mail={mail}
          onClose={() => setShowConvert(false)}
          onConverted={(t) => { setConvertedTicket(t); onChanged(); }}
        />
      )}
    </>
  );
}
